import React, { createContext, useState, useEffect, useContext } from 'react';
import { getAppointments, createAppointment, updateAppointment, deleteAppointment, confirmAppointment as confirmAppointmentAPI, cancelAppointment as cancelAppointmentAPI, markAppointmentMessageSent, normalizeAppointmentData } from '../firebase/appointments';
import { useSalon } from './SalonContext';

// Criação do contexto
const AppointmentContext = createContext(null);

// Hook personalizado para usar o contexto
export const useAppointments = () => {
  const context = useContext(AppointmentContext);
  if (context === null) {
    throw new Error('useAppointments deve ser usado dentro de um AppointmentProvider');
  }
  return context;
};

// Provedor do contexto
export const AppointmentProvider = ({ children }) => {
  const { salonId } = useSalon();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Implementar cache para agendamentos por salonId
  const appointmentsCache = new Map();
  const CACHE_EXPIRY_TIME = 5 * 60 * 1000; // 5 minutos

  // Função otimizada para carregar agendamentos com cache
  const loadAppointmentsWithCache = async (salonId) => {
    // Verificar se há dados em cache e se o cache ainda é válido
    const cachedData = appointmentsCache.get(salonId);
    const now = Date.now();
    
    if (cachedData && (now - cachedData.timestamp < CACHE_EXPIRY_TIME)) {
      // Usar dados do cache se ainda forem válidos
      return cachedData.data;
    }
    
    // Se não houver cache válido, buscar do Firebase
    try {
      setLoading(true);
      const appointments = await getAppointments(salonId);
      
      // Atualizar o cache
      appointmentsCache.set(salonId, {
        data: appointments,
        timestamp: now
      });
      
      return appointments;
    } catch (error) {
      console.error('Erro ao carregar agendamentos:', error);
      setError(error.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Função para invalidar o cache quando ocorrem mudanças
  const invalidateAppointmentsCache = (salonId) => {
    appointmentsCache.delete(salonId);
  };

  // Carregar agendamentos
  useEffect(() => {
    if (!salonId) return;
    
    // Usar a função com cache para carregar os dados
    loadAppointmentsWithCache(salonId)
      .then(setAppointments)
      .catch(error => {
        console.error('Erro ao carregar agendamentos:', error);
        setError(error.message);
      });
  }, [salonId]);

  // Função para adicionar um novo agendamento
  const addAppointment = async (appointmentData) => {
    try {
      setError(null);
      
      // LOG DETALHADO - mostrar dados brutos recebidos
      console.log('=== DADOS BRUTOS DE AGENDAMENTO ===');
      console.log('appointmentData:', JSON.stringify(appointmentData));
      console.log('data:', appointmentData.data, 'tipo:', typeof appointmentData.data);
      console.log('dataISO:', appointmentData.dataISO, 'tipo:', typeof appointmentData.dataISO);
      console.log('dataFormatada:', appointmentData.dataFormatada, 'tipo:', typeof appointmentData.dataFormatada);
      
      // Normalizar dados antes de passar para o Firebase
      const normalizedData = normalizeAppointmentData(appointmentData);
      
      // LOG DETALHADO - mostrar dados normalizados
      console.log('=== DADOS NORMALIZADOS ===');
      console.log('normalizedData:', JSON.stringify(normalizedData));
      console.log('data normalizada:', normalizedData.data, 'tipo:', typeof normalizedData.data);
      
      // Adicionar no Firestore
      const newAppointment = await createAppointment(salonId, normalizedData);
      
      // Invalidar o cache após alteração
      invalidateAppointmentsCache(salonId);
      
      // Recarregar os dados do Firebase
      const updatedAppointments = await loadAppointmentsWithCache(salonId);
      setAppointments(updatedAppointments);
      
      return newAppointment;
    } catch (err) {
      console.error('Erro ao adicionar agendamento:', err);
      setError(err.message || 'Erro ao adicionar agendamento');
      return null;
    }
  };

  // Função para atualizar um agendamento
  const updateAppointmentData = async (appointmentId, updates) => {
    try {
      setError(null);
      
      // Normalizar os dados antes de atualizar
      const normalizedUpdates = normalizeAppointmentData(updates);
      
      // Atualizar no Firestore
      await updateAppointment(salonId, appointmentId, normalizedUpdates);
      
      // Atualizar estado local
      setAppointments(prev => 
        prev.map(app => 
          app.id === appointmentId 
            ? { ...app, ...normalizedUpdates, updatedAt: new Date() } 
            : app
        )
      );
      
      // Invalidar o cache após modificação
      invalidateAppointmentsCache(salonId);
      
      return true;
    } catch (err) {
      console.error('Erro ao atualizar agendamento:', err);
      setError(err.message || 'Erro ao atualizar agendamento');
      return false;
    }
  };

  // Função para remover um agendamento
  const removeAppointment = async (appointmentId) => {
    try {
      setError(null);
      
      // Remover do Firestore
      await deleteAppointment(salonId, appointmentId);
      
      // Atualizar estado local
      setAppointments(prev => prev.filter(app => app.id !== appointmentId));
      
      // Invalidar o cache após modificação
      invalidateAppointmentsCache(salonId);
      
      return true;
    } catch (err) {
      console.error('Erro ao remover agendamento:', err);
      setError(err.message || 'Erro ao remover agendamento');
      return false;
    }
  };

  // Função para confirmar um agendamento
  const confirmAppointment = async (appointmentId) => {
    try {
      setError(null);
      
      // Confirmar no Firestore
      await confirmAppointmentAPI(salonId, appointmentId);
      
      // Atualizar estado local
      setAppointments(prev => 
        prev.map(app => 
          app.id === appointmentId 
            ? { ...app, confirmado: true, updatedAt: new Date() } 
            : app
        )
      );
      
      // Invalidar o cache após modificação
      invalidateAppointmentsCache(salonId);
      
      return true;
    } catch (err) {
      console.error('Erro ao confirmar agendamento:', err);
      setError(err.message || 'Erro ao confirmar agendamento');
      return false;
    }
  };

  // Função para cancelar um agendamento
  const cancelAppointment = async (appointmentId) => {
    try {
      setError(null);
      
      // Cancelar no Firestore
      await cancelAppointmentAPI(salonId, appointmentId);
      
      // Atualizar estado local
      setAppointments(prev => 
        prev.map(app => 
          app.id === appointmentId 
            ? { ...app, cancelado: true, confirmado: false, updatedAt: new Date() } 
            : app
        )
      );
      
      // Invalidar o cache após modificação
      invalidateAppointmentsCache(salonId);
      
      return true;
    } catch (err) {
      console.error('Erro ao cancelar agendamento:', err);
      setError(err.message || 'Erro ao cancelar agendamento');
      return false;
    }
  };

  // Função para marcar mensagem como enviada
  const markMessageSent = async (appointmentId) => {
    try {
      setError(null);
      
      // Marcar no Firestore
      await markAppointmentMessageSent(salonId, appointmentId);
      
      // Atualizar estado local
      setAppointments(prev => 
        prev.map(app => 
          app.id === appointmentId 
            ? { ...app, mensagemEnviada: true, updatedAt: new Date() } 
            : app
        )
      );
      
      // Invalidar o cache após modificação
      invalidateAppointmentsCache(salonId);
      
      return true;
    } catch (err) {
      console.error('Erro ao marcar mensagem como enviada:', err);
      setError(err.message || 'Erro ao marcar mensagem como enviada');
      return false;
    }
  };

  // Objeto com valores e funções a serem disponibilizadas pelo contexto
  const value = {
    appointments,
    loading,
    error,
    addAppointment,
    updateAppointment: updateAppointmentData,
    removeAppointment,
    confirmAppointment,
    cancelAppointment,
    markMessageSent
  };

  return (
    <AppointmentContext.Provider value={value}>
      {children}
    </AppointmentContext.Provider>
  );
};

export default AppointmentContext; 